
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getPaymentsByReservation, 
  getAllPayments, 
  markPaymentAsPaid,
  updatePaymentNotes,
  updatePaymentDueDate,
  createPaymentSession,
  createPaymentRecords,
  cancelAutomaticPayments,
  deletePayment
} from '@/services/paymentService';
import type { Payment } from '@/types/payments';
import { toast } from '@/components/ui/use-toast';

export const usePaymentsByReservation = (reservationId: string) => {
  return useQuery({
    queryKey: ['payments', reservationId],
    queryFn: () => getPaymentsByReservation(reservationId),
    enabled: !!reservationId,
  });
};

export const useAllPayments = () => {
  return useQuery({
    queryKey: ['payments'],
    queryFn: getAllPayments,
  });
};

export const useCreatePaymentRecords = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createPaymentRecords,
    onSuccess: (data, reservationId) => {
      queryClient.invalidateQueries({ queryKey: ['payments', reservationId] });
      toast({
        title: "Paiements créés",
        description: "Les échéances de paiement ont été configurées.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de créer les paiements.",
        variant: "destructive",
      });
    },
  });
};

export const useMarkPaymentAsPaid = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      paymentId, 
      paymentMethod, 
      notes 
    }: { 
      paymentId: string; 
      paymentMethod: Payment['payment_method']; 
      notes?: string; 
    }) => markPaymentAsPaid(paymentId, paymentMethod, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast({
        title: "Paiement marqué comme payé",
        description: "Le statut du paiement a été mis à jour avec succès.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour le paiement.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdatePaymentNotes = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ paymentId, notes }: { paymentId: string; notes: string }) => 
      updatePaymentNotes(paymentId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast({
        title: "Notes mises à jour",
        description: "Les notes du paiement ont été mises à jour.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour les notes.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdatePaymentDueDate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ paymentId, dueDate }: { paymentId: string; dueDate: string }) => 
      updatePaymentDueDate(paymentId, dueDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast({
        title: "Échéance modifiée",
        description: "La date d'échéance du paiement a été mise à jour.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour l'échéance.",
        variant: "destructive",
      });
    },
  });
};

export const useCreatePaymentSession = () => {
  return useMutation({
    mutationFn: createPaymentSession,
    onError: (error: any) => {
      toast({
        title: "Erreur de paiement",
        description: error.message || "Impossible de créer la session de paiement.",
        variant: "destructive",
      });
    },
  });
};

export const useCancelAutomaticPayments = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: cancelAutomaticPayments,
    onSuccess: (_, reservationId) => {
      queryClient.invalidateQueries({ queryKey: ['payments', reservationId] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast({
        title: "Prélèvements annulés",
        description: "Les prélèvements automatiques ont été annulés avec succès.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'annuler les prélèvements automatiques.",
        variant: "destructive",
      });
    },
  });
};

export const useDeletePayment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deletePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast({
        title: "Paiement supprimé",
        description: "L'échéance de paiement a été supprimée avec succès.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer le paiement.",
        variant: "destructive",
      });
    },
  });
};
